import React, { useState, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'store/hooks';
import { useNavigate } from 'react-router-dom';
import { AppState } from '../../../../../store';
import { signSetAction } from '../../../../../store/signup/actions';
import SignupTextField from '../../../components/SignupTextField';
import { useTranslation } from 'react-i18next';
import { Organization } from '../../../../../store/signup/types';
import { PATH } from '../../../../../routes';
import { zipRegExp } from '../../../utils/validationRegExp';
import { OkButton } from '../../../components/TextFieldWrapper';
import { simpleValidation } from '../../../components/SimpleTextFieldComponent';
import Space from '../../../../../theme/Space';
import Grid from '@material-ui/core/Grid/Grid';
import StateDropDownField from '../../../components/StateDropDownField';
import { useRouteConfig } from '../../../utils/useRouteConfig';
import {
    useOrganizationAutoCompleteStyles,
    zipValidation,
} from '../../../utils/organizationAutoCompleteUtils';
import { phoneFormatter, phoneValidation } from '@givelify/givelify-ui';
import { pushEvent } from 'utils/clevertapUtils';
import {
    CLICK_ORG_MANUAL_ENTRY_PAGE_OK_BTN,
    VIEW_ORG_MANUAL_ENTRY_PAGE,
} from 'utils/clevertapEvents';
import { Features } from 'store/featureFlags/types';
import { normalizeApostrophes } from 'pages/signup/utils/normalizeApostrophes';
import { SEARCH_EXPERIMENT, SEARCH_UI_EXPERIMENT } from 'utils/clevertapVariantFlags';

const OrganizationManualEntryForm: React.FC = () => {
    const dispatch = useAppDispatch();
    const push = useNavigate();
    const { t } = useTranslation();
    const { gridStyles } = useOrganizationAutoCompleteStyles();
    const defaultOrganizationValues: Organization = useSelector(
        (state: AppState) => ({
            organizationLocation: state.Signup.organizationLocation,
            organizationName: state.Signup.organizationName,
            organizationPhoneNumber: state.Signup.organizationPhoneNumber,
            organizationAddress: state.Signup.organizationAddress,
            organizationZip: state.Signup.organizationZip,
            doneeId: state.Signup.doneeId,
            doneeType: state.Signup.doneeType,
            organizationCity: state.Signup.organizationCity,
            organizationState: state.Signup.organizationState,
            ypId: state.Signup.ypId,
            lat: state.Signup.lat,
            lon: state.Signup.lon,
        }),
    );
    const v2SearchAPIEnabled = useSelector(
        (state: AppState) => state.FeatureFlagging.features[Features.SEARCH_API_V2]
    );

    useEffect(() => {
        pushEvent(VIEW_ORG_MANUAL_ENTRY_PAGE, {
            [SEARCH_EXPERIMENT]: v2SearchAPIEnabled ? 'on' : 'off',
            [SEARCH_UI_EXPERIMENT]: 'off',
        });
    }, [v2SearchAPIEnabled]);

    const { next } = useRouteConfig(PATH.SIGNUP.STEPS.ADD_ORGANIZATION);

    const copy = useMemo(
        () => ({
            placeholderName: t('signup.organizationName.placeholders.name'),
            placeholderAddress: t(
                'signup.organizationName.placeholders.address',
            ),
            placeholderZip: t('signup.organizationName.placeholders.zip'),
            placeholderPhone: t('signup.organizationName.placeholders.phone'),
            placeholderCity: t('signup.organizationName.placeholders.city'),
            placeholderState: t('signup.organizationName.placeholders.state'),
        }),
        [t],
    );

    const [organization, setOrganization] = useState<Organization>(
        defaultOrganizationValues,
    );

    const onChange = (value: string, name?: string) => {
        if (!name) return;
        setOrganization({
            ...organization,
            [name]: normalizeApostrophes(value),
        });
    };

    const handlePhoneNumber = (value: string) => {
        const phone = phoneFormatter(value);
        setOrganization({
            ...organization,
            organizationPhoneNumber: phone || '',
        });
    };

    const handleStateChange = (value: string) => {
        setOrganization({ ...organization, organizationState: value });
    };

    const onSubmit = () => {
        pushEvent(CLICK_ORG_MANUAL_ENTRY_PAGE_OK_BTN);
        dispatch(signSetAction(organization));
        next && push(next);
    };

    const isFormValid =
        !!organization.organizationName &&
        !!organization.organizationAddress &&
        !!organization.organizationCity &&
        organization.organizationState.length === 2 &&
        phoneValidation(organization.organizationPhoneNumber) &&
        zipRegExp.test(organization.organizationZip);

    return (
        <>
            <Space gap={3} />
            <SignupTextField
                name="organizationName"
                isFocus={true}
                value={organization.organizationName}
                type="text"
                placeholder={copy.placeholderName}
                onChange={onChange}
                validation={simpleValidation}
                maxLength={255}
            />
            <Space gap={3} />
            <SignupTextField
                name="organizationAddress"
                isFocus={undefined}
                value={organization.organizationAddress}
                type="text"
                placeholder={copy.placeholderAddress}
                onChange={onChange}
                validation={simpleValidation}
                maxLength={255}
            />
            <Space gap={3} />
            <SignupTextField
                name="organizationZip"
                isFocus={undefined}
                value={organization.organizationZip}
                type="text"
                placeholder={copy.placeholderZip}
                onChange={onChange}
                validation={zipValidation}
                maxLength={5}
            />
            <Space gap={3} />
            <Grid container className={gridStyles}>
                <Grid item xs={12} sm={6} md={6} lg={6} xl={6} className="left">
                    <SignupTextField
                        name="organizationCity"
                        isFocus={undefined}
                        value={organization.organizationCity}
                        type="text"
                        placeholder={copy.placeholderCity}
                        onChange={onChange}
                        validation={simpleValidation}
                        className="text-field"
                        maxLength={55}
                    />
                </Grid>
                <Grid
                    item
                    xs={12}
                    sm={6}
                    md={6}
                    lg={6}
                    xl={6}
                    className="right"
                >
                    <StateDropDownField
                        onChange={handleStateChange}
                        validation={simpleValidation}
                    />
                </Grid>
            </Grid>
            <Space gap={3} />
            <SignupTextField
                name="organizationPhoneNumber"
                isFocus={undefined}
                value={organization.organizationPhoneNumber}
                type="text"
                placeholder={copy.placeholderPhone}
                onChange={handlePhoneNumber}
                validation={phoneValidation}
                maxLength={14}
            />
            {isFormValid && <OkButton onSubmit={onSubmit} />}
        </>
    );
};

export default React.memo(OrganizationManualEntryForm);
